import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Bell, Check, CheckCircle, MessageSquare, AlertCircle, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  getNotificationUrl,
  type Notification,
} from '@/shared/hooks/use-notifications'

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface NotificationItemProps {
  notification: Notification
  onNavigate: () => void
}

function getNotificationIcon(type: string, isRead: boolean) {
  const colorClass = isRead ? 'text-foreground-muted' : 'text-primary'

  switch (type) {
    case 'project_validated':
      return <CheckCircle className={cn('h-4 w-4', isRead ? 'text-foreground-muted' : 'text-green-600')} />
    case 'project_rejected':
      return <AlertCircle className={cn('h-4 w-4', isRead ? 'text-foreground-muted' : 'text-amber-600')} />
    default:
      return <MessageSquare className={cn('h-4 w-4', colorClass)} />
  }
}

function getNotificationIconBg(type: string, isRead: boolean) {
  if (isRead) return 'bg-background-secondary'

  switch (type) {
    case 'project_validated':
      return 'bg-green-100'
    case 'project_rejected':
      return 'bg-amber-100'
    default:
      return 'bg-primary/10'
  }
}

function NotificationItem({ notification, onNavigate }: NotificationItemProps) {
  const navigate = useNavigate()
  const { mutate: markAsRead } = useMarkNotificationRead()

  const handleClick = () => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
    const url = getNotificationUrl(notification)
    navigate(url)
    onNavigate()
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-background-secondary',
        !notification.is_read && 'bg-primary/5'
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
          getNotificationIconBg(notification.type, notification.is_read)
        )}
      >
        {getNotificationIcon(notification.type, notification.is_read)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p
          className={cn(
            'text-sm',
            notification.is_read ? 'text-foreground-muted' : 'font-medium text-foreground'
          )}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="truncate text-xs text-foreground-muted">{notification.body}</p>
        )}
        <p className="text-xs text-foreground-muted/70">{formatTimeAgo(notification.created_at)}</p>
      </div>
      {!notification.is_read && (
        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
      )}
    </button>
  )
}

export function NotificationNavItem() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { data: notifications = [], isLoading } = useNotifications(20)
  const { data: unreadCount = 0 } = useUnreadNotificationCount()
  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllNotificationsRead()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleNavigate = () => {
    setIsOpen(false)
  }

  const handleMarkAllRead = () => {
    markAllAsRead()
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Nav item trigger - styled like NavItem */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-6 py-3 text-left text-sm font-medium transition-colors',
          isOpen
            ? 'bg-white/10 text-white'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        )}
      >
        <Bell className="h-5 w-5" />
        <span className="flex-1">{t('nav.notifications')}</span>
        {unreadCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-2 text-[11px] font-semibold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel - opens to the right */}
      {isOpen && (
        <div className="absolute left-full top-0 z-50 ml-2 w-80 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-left-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={isMarkingAll}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                  title={t('notifications.markAllRead')}
                >
                  <Check className="h-3 w-3" />
                  {t('notifications.markAllRead')}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <Bell className="h-8 w-8 text-foreground-muted/50" />
                <p className="text-sm text-foreground-muted">{t('notifications.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
